import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";

/**
 * Delay before the tooltip appears, in milliseconds. Long enough that a
 * cursor merely passing over a button never triggers one, short enough
 * that a real "what does this do?" hover pops up fast — the audit
 * finding on the native `title` tooltip pegged that at 300–400 ms.
 */
const SHOW_DELAY_MS = 350;

/**
 * Exit-animation window — matches `--transition-base` (0.15s), the
 * same token `.tooltip`'s CSS enter animation uses. Same reasoning
 * as `Dialog.tsx`/`DropdownMenu.tsx`'s own exit constants: driven
 * from JS so the hide-timer doesn't need a `getComputedStyle` round-
 * trip to the root every pointer-leave.
 */
const HIDE_DELAY_MS = 150;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Custom tooltip wrapper — replaces the browser's native `title` on icon-
 * only buttons (which shows after ~1 s and can't be styled). Applied
 * indirectly, by wrapping this around `IconButton`/`DropdownMenu`'s trigger
 * — both are the funnels every icon-only button in AppBar/ToolRail and
 * every icon-only dropdown trigger (Landing's Ajustes gear included) go
 * through, so touching those two funnels covers the audit's called-out
 * sites without hunting individual callsites.
 *
 * Rules of the road:
 *  - `aria-label` on the trigger is the accessible name; this tooltip is
 *    a purely-visual affordance for sighted users, so we deliberately do
 *    NOT wire `aria-describedby` too. A screen reader announcing both
 *    would read the same phrase twice for the very common case where
 *    `label` and the trigger's own aria-label match.
 *  - The wrapper itself listens to pointer/focus events, not the child —
 *    so a disabled `<button>` (which drops many events) still shows its
 *    tooltip on hover. Useful precisely BECAUSE users often want to know
 *    "why is this disabled?".
 *  - The tooltip node itself is rendered through a `createPortal` into
 *    `document.body`, not left inline next to the trigger — several
 *    ancestors of a real button in the app clip overflow (`.topbar`'s
 *    own `overflow-y: hidden` for its title ellipsis, `.tool-rail`'s
 *    own `overflow-y: auto` computed side-effect on `overflow-x`), and
 *    any inline tooltip that tries to escape THEIR boxes gets clipped
 *    or hidden behind higher-stack-context siblings regardless of its
 *    own `z-index`. Portalling to `<body>` sidesteps every one of those.
 *  - `role="tooltip"` + `pointer-events: none` on the tooltip node so
 *    the tooltip never eats a click intended for the trigger, and its
 *    own hitbox can't re-trigger `pointerenter` on the wrapper.
 */
/**
 * Distance in pixels the tooltip is kept away from the viewport's left/
 * right edges — so a ToolRail button at x≈28 doesn't render its tooltip
 * with its leftmost characters clipped off-screen.
 */
const VIEWPORT_INSET = 8;

/**
 * Second-audit finding follow-up: the tooltip already fades in
 * (point 3) but was disappearing instantly on `hide` — inconsistent
 * with its own arrival and with the dialog/popover exit pass. Three
 * phases now — hidden (unmounted), visible (fully painted), exiting
 * (mounted with `.tooltip-exiting` playing the fade-out for one
 * `HIDE_DELAY_MS` window before the real unmount). A fresh hover
 * during an exit cancels the timer and re-shows immediately.
 */
type TooltipPhase = "hidden" | "visible" | "exiting";

export function Tooltip({ label, children }: { label: string; children: ReactElement }) {
  const [phase, setPhase] = useState<TooltipPhase>("hidden");
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const showTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  function clearShowTimer() {
    if (showTimerRef.current !== null) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  }
  function clearHideTimer() {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }

  function show() {
    // Re-hover during the fade-out cancels it — the tooltip is already on
    // screen, no reason to unmount and then re-show after another 350 ms.
    if (phase === "exiting") {
      clearHideTimer();
      setPhase("visible");
      return;
    }
    if (phase === "visible" || showTimerRef.current !== null) return;
    showTimerRef.current = window.setTimeout(() => {
      showTimerRef.current = null;
      setPhase("visible");
    }, SHOW_DELAY_MS);
  }
  function hide() {
    clearShowTimer();
    if (phase !== "visible") {
      // Was still waiting on the show delay (or already gone) — just drop
      // back to hidden without paying for the fade-out.
      setPhase("hidden");
      return;
    }
    if (prefersReducedMotion()) {
      setPhase("hidden");
      return;
    }
    setPhase("exiting");
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      setPhase("hidden");
    }, HIDE_DELAY_MS);
  }
  // Cancels any still-pending timer if the wrapper unmounts mid-hover /
  // mid-exit — a stray setState on an unmounted node would otherwise log
  // a warning.
  useEffect(
    () => () => {
      clearShowTimer();
      clearHideTimer();
    },
    []
  );

  const mounted = phase !== "hidden";

  // Reads the wrapper's live position AFTER `mounted` flips true so the
  // portalled tooltip lands under the current trigger, not wherever a
  // stale position said. `useLayoutEffect` (not plain `useEffect`) so
  // the coordinates are set in the same paint frame the tooltip first
  // renders in — otherwise it flashes for one frame at (0, 0) before
  // snapping into place. Skipped on the visible→exiting transition to
  // hold the last-computed position instead of re-measuring a wrapper
  // that may have moved by then.
  useLayoutEffect(() => {
    if (phase !== "visible") {
      if (phase === "hidden") setPos(null);
      return;
    }
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ left: rect.left + rect.width / 2, top: rect.bottom + 6 });
  }, [phase]);

  // Second pass: once `pos` above lands and the tooltip actually rendered,
  // clamp its horizontal center so it never crosses the viewport edges — a
  // ToolRail button sits at x≈28 px on the left, so a naively centered
  // tooltip runs off-screen there. Terminates naturally: after one
  // adjustment `pos.left` is already inside `[min, max]`, so the second
  // run of this effect computes `clamped === pos.left` and skips the
  // setState.
  useLayoutEffect(() => {
    if (!pos || !tooltipRef.current) return;
    const width = tooltipRef.current.offsetWidth;
    const vw = document.documentElement.clientWidth;
    const min = width / 2 + VIEWPORT_INSET;
    const max = vw - width / 2 - VIEWPORT_INSET;
    const clamped = Math.max(min, Math.min(max, pos.left));
    if (clamped !== pos.left) setPos((prev) => (prev ? { ...prev, left: clamped } : prev));
  }, [pos]);

  return (
    <span
      ref={wrapperRef}
      className="tooltip-wrapper"
      onPointerEnter={show}
      onPointerLeave={hide}
      onFocusCapture={show}
      onBlurCapture={hide}
    >
      {children}
      {mounted &&
        pos &&
        createPortal(
          <span
            ref={tooltipRef}
            role="tooltip"
            className={phase === "exiting" ? "tooltip tooltip-exiting" : "tooltip"}
            style={{ left: pos.left, top: pos.top }}
          >
            {label}
          </span>,
          document.body
        )}
    </span>
  );
}
